/* eslint-disable react-refresh/only-export-components -- bewusst co-lozierter Provider+Hook (analog theme.tsx); react-refresh ist eine reine Dev-HMR-Optimierung. */
import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

/**
 * Anzeige-Modus für Formularfelder.
 *
 * - `profi`:    kompakte Felder, Hilfe nur per Hover-Icon.
 * - `standard`: blendet zu jedem Feld mit `helpKey` eine ausführliche
 *               Inline-Erklärung inkl. Tipps ein (Quelle: Tooltip-Inventory).
 * - `gefuehrt`: maximale Ausfüll-Unterstützung — wie `standard`, zusätzlich
 *               führt das Projektformular Abschnitt für Abschnitt durch die
 *               Eingabe (Stepper mit Fortschritt, siehe ProjectForm).
 *
 * Persistiert in localStorage (analog Theme). Default: `profi`, damit das
 * bestehende Verhalten erhalten bleibt — Nutzer schalten Unterstützung
 * bewusst zu.
 */
export type DisplayMode = 'gefuehrt' | 'standard' | 'profi';

const STORAGE_KEY = 'ignisai.displayMode';
const DEFAULT_MODE: DisplayMode = 'profi';

interface DisplayModeContextValue {
  mode: DisplayMode;
  setMode: (m: DisplayMode) => void;
  toggle: () => void;
  /** Bequemer Abfrage-Shortcut: geführter Modus aktiv? */
  guided: boolean;
  /** Schaltet den geführten Modus ein/aus (aus → `standard`, Hilfen bleiben sichtbar). */
  setGuided: (on: boolean) => void;
}

const DisplayModeContext = createContext<DisplayModeContextValue | null>(null);

function persist(mode: DisplayMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode);
  } catch {
    /* private mode etc. — Modus bleibt für die Session im State. */
  }
}

export function DisplayModeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<DisplayMode>(() => {
    if (typeof window === 'undefined') return DEFAULT_MODE;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    return stored === 'standard' || stored === 'profi' || stored === 'gefuehrt'
      ? stored
      : DEFAULT_MODE;
  });

  const setMode = useCallback((next: DisplayMode) => {
    setModeState(next);
    persist(next);
  }, []);

  const toggle = useCallback(() => {
    setModeState((prev) => {
      // Bestandsverhalten: schaltet die Inline-Hilfen an/aus. Aus dem
      // geführten Modus heraus landet man bei „aus" (profi).
      const next: DisplayMode = prev === 'profi' ? 'standard' : 'profi';
      persist(next);
      return next;
    });
  }, []);

  const setGuided = useCallback((on: boolean) => setMode(on ? 'gefuehrt' : 'standard'), [setMode]);

  const value = useMemo<DisplayModeContextValue>(
    () => ({ mode, setMode, toggle, guided: mode === 'gefuehrt', setGuided }),
    [mode, setMode, toggle, setGuided],
  );

  return <DisplayModeContext.Provider value={value}>{children}</DisplayModeContext.Provider>;
}

/**
 * Liefert den aktuellen Anzeige-Modus. Ohne Provider (z. B. isolierte Tests)
 * fällt der Hook still auf den Profi-Modus zurück, statt zu werfen — `Input`,
 * `Select` & Co. sind UI-Primitive und sollen überall nutzbar bleiben.
 */
export function useDisplayMode(): DisplayModeContextValue {
  const ctx = useContext(DisplayModeContext);
  if (ctx) return ctx;
  return {
    mode: DEFAULT_MODE,
    setMode: () => {},
    toggle: () => {},
    guided: false,
    setGuided: () => {},
  };
}
